import { NativeGeocoder } from "@capgo/nativegeocoder";
import { Geolocation } from "@capacitor/geolocation";
import { countryCodes } from "../../utils/constants";
import { Device } from "@capacitor/device";
import {
  AirQualityData,
  getEnvironmentalData,
  WeatherData,
} from "../../services/external";
import { updateHandset } from "../../services/auth";
import { uploadHealthDataToServer } from "../../services/healthkit";
import {
  getIonicStorage,
  getStoredPreferences,
  ionicStorage,
  storePrefrences,
} from "../../services/storageService";
import { fetchLocation } from "../../utils/AllCountryCodes";
import { Storage } from "@ionic/storage";

class EnvironmentalDataManager {
  static weatherData: (WeatherData & AirQualityData) | null = null;
  static loadWeatherData = async () => {
    if (navigator.geolocation) {
      const lastFetched = await getStoredPreferences("lastWeatherFetch");
      const now = new Date();
      if (lastFetched.value) {
        const lastDate = new Date(lastFetched.value);
        const diffMinutes = (now.getTime() - lastDate.getTime()) / 60000;
        if (diffMinutes < 30) {
          console.log("Weather data fetched recently, skipping fetch.");
          return; // Skip fetching if last fetch was within 30 minutes
        }
      }
      storePrefrences("lastWeatherFetch", now.toISOString());
      navigator.geolocation.getCurrentPosition(async (position) => {
        // You may want to show a toast or message here in your UI
        try {
          const { weather, airQuality } = await getEnvironmentalData(
            position.coords.latitude,
            position.coords.longitude
          );

          // Replace setWeatherData with a console log or handle as needed
          console.log("Weather and Air Quality Data:", {
            ...airQuality,
            ...weather,
          });
          const payload: any = {
            time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone, //Use current time zone if not available
            activity_type: "aqi",
            start_date: new Date().toISOString(),
            end_date: new Date().toISOString(),
            uuid: crypto.randomUUID(),
            source: (await Device.getId()) + "openAPI",
            source_bundle_id: "openAPI",
            numeric_value: airQuality.aqi,
            metadata: { ...airQuality, ...weather },
          };
          console.log("Uploading environmental data payload:", payload);

          // Send data to server using your apiClient
          await uploadHealthDataToServer([payload])
            .then((response) => {
              // Log the success
              console.log(
                "Health data uploaded successfully:",
                response.data,
                payload
              );
            })
            .catch((error) => {
              // Log the error
              console.error("Error uploading health data:", error);
            });
        } catch (error) {
          // Replace setToastMessage and setShowToast with a console log or handle as needed
          console.error("Failed to fetch environmental data", error);
        }
      });
    }
  };
  /**
   * Fetches user location and weather/environmental data
   * Updates device information in backend
   */
  static async fetchWeatherData(
    user: any | null,
    storage: Storage | null
  ): Promise<void> {
    try {
      // Check the last fetched time to avoid frequent calls from the preferences
      if (storage) {
        const lastFetched = await getIonicStorage(storage, "lastWeatherFetch");
        // console.log("Last fetched weather data at:", lastFetched);
        const now = new Date();
        if (lastFetched) {
          const lastDate = new Date(lastFetched);
          const diffMinutes = (now.getTime() - lastDate.getTime()) / 60000;
          if (diffMinutes < 30) {
            console.log("Weather data fetched recently, skipping fetch.");
            return; // Skip fetching if last fetch was within 30 minutes
          }
        }
      }
      // console.log("Fetching new weather data...", storage, new Date().toISOString());
      if (storage) {
        await ionicStorage(
          storage,
          "lastWeatherFetch",
          new Date().toISOString()
        );
      }
      // Get current position using @capacitor/geolocation
      const position = await Geolocation.getCurrentPosition();

      // Use @capgo/nativegeocoder for reverse geocoding
      const geocodeResult = await NativeGeocoder.reverseGeocode({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });

      // Extract address information
      const address = geocodeResult.addresses[0];

      // Get environmental data (weather and air quality)
      // const { weather, airQuality } = await getEnvironmentalData(
      //   position.coords.latitude,
      //   position.coords.longitude
      // );

      // this.weatherData = { ...airQuality, ...weather };
      // console.log("Environmental data fetched:", this.weatherData);
      // // upload weather data to the server
      // if (this.weatherData) {
      //   const payload: any = {
      //     time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone, //Use current time zone if not available
      //     activity_type: "aqi",
      //     start_date: new Date().toISOString(),
      //     end_date: new Date().toISOString(),
      //     uuid: crypto.randomUUID(),
      //     source: (await Device.getId()) + "openAPI",
      //     source_bundle_id: "openAPI",
      //     numeric_value: this.weatherData.aqi,
      //     metadata: { ...this.weatherData },
      //   };

      //   // Send data to server using your apiClient
      //   await uploadHealthDataToServer([payload])
      //     .then((response) => {
      //       // Log the success
      //       console.log(
      //         "Health data uploaded successfully:",
      //         response.data,
      //         payload
      //       );
      //     })
      //     .catch((error) => {
      //       // Log the error
      //       console.error("Error uploading health data:", error);
      //     });
      // }

      // Update device info in backend if user is logged in
      if (user?.profile_id) {
        await updateHandset({
          handset_id: (await Device.getId()).identifier,
          token: "",
          profile_id: user.profile_id,
          login_source: user?.socialProvider || "phone",
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          location: `${address.locality}, ${address.countryName}`,
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
    } catch (error) {
      console.error("Error in fetchWeatherData:", error);
      throw error;
    }
  }

  /**
   * Converts AQI to human-readable air quality label
   */
  static getAirQualityLabel(aqi: number): string {
    if (aqi <= 1) return "Good";
    if (aqi <= 2) return "Moderate";
    if (aqi <= 3) return "Low Risk";
    if (aqi <= 4) return "Unhealthy";
    if (aqi <= 5) return "Very Unhealthy";
    return "Hazardous";
  }
}

export const getCountryDetails = async () => {
  const position = await Geolocation.getCurrentPosition();
  const lat = position.coords.latitude;
  const lng = position.coords.longitude;

  // Reverse geocoding API
  const data = await fetchLocation(lat, lng);

  const country = data.address.country;
  console.log("Country from geocoding:", country);
  const matchedCountries = countryCodes.filter(
    (c) => c.country.toLowerCase() === country.toLowerCase()
  );
  return matchedCountries;
};

export default EnvironmentalDataManager;
